/**
 * A small, dependency-free SPF (RFC 7208) policy parser.
 *
 * It turns a published policy string (e.g. `"v=spf1 include:_spf.google.com ip4:198.51.100.0/24 ~all"`)
 * into a structured decomposition the SPF tab renders: the ordered list of sender-authorizing
 * mechanisms, the trailing `all` directive that defines the *final policy*, any modifiers
 * (`redirect` / `exp`), and a set of human-readable warnings for common misconfigurations.
 *
 * This is a *display* aid, not a validator/evaluator — it never performs DNS lookups and is lenient
 * about malformed input (it records a warning rather than throwing) so the tab can always render
 * *something* for whatever the zone actually publishes.
 */

export type SpfQualifier = '+' | '-' | '~' | '?';

export type SpfMechanismType = 'all' | 'include' | 'a' | 'mx' | 'ptr' | 'ip4' | 'ip6' | 'exists' | 'unknown';

export type SpfModifierType = 'redirect' | 'exp' | 'unknown';

/** Severity hint for UI coloring (maps onto PatternFly Label/Alert colors). */
export type SpfSeverity = 'success' | 'info' | 'warning' | 'danger';

export interface SpfMechanism {
  /** The original token, verbatim. */
  raw: string;
  qualifier: SpfQualifier;
  /** `'Pass' | 'Fail' | 'SoftFail' | 'Neutral'`. */
  qualifierLabel: string;
  type: SpfMechanismType;
  /** The argument after the `:` (domain / IP / CIDR), or `null` for a bare `a`/`mx`/`ptr`/`all`. */
  value: string | null;
  /** Plain-English meaning of the mechanism. */
  description: string;
}

export interface SpfModifier {
  raw: string;
  type: SpfModifierType;
  /** The modifier name (`redirect`, `exp`, or the unknown name). */
  name: string;
  value: string;
  description: string;
}

export interface SpfPolicy {
  qualifier: SpfQualifier;
  /** Short label, e.g. `'Fail (reject)'`. */
  label: string;
  description: string;
  severity: SpfSeverity;
  /** `true` when the policy is delegated to another domain via a `redirect=` modifier. */
  delegated: boolean;
}

export interface ParsedSpf {
  /** `true` when the string is a well-formed SPF record (starts with `v=spf1`). */
  valid: boolean;
  /** The version token, e.g. `'spf1'`, or `null` when invalid. */
  version: string | null;
  /** Sender-authorizing directives (every mechanism except the trailing `all`), in document order. */
  mechanisms: SpfMechanism[];
  /** The `all` directive that terminates the record, or `null` when absent. */
  all: SpfMechanism | null;
  /** The effective final policy, derived from `all` (or a `redirect` when no `all` is present). */
  policy: SpfPolicy | null;
  /** `redirect` / `exp` (and any unknown) modifiers. */
  modifiers: SpfModifier[];
  /** Convenience: the `redirect=` target, if any. */
  redirect: string | null;
  /** Human-readable advisories about misconfigurations / weak settings. */
  warnings: string[];
  /** The input, trimmed. */
  raw: string;
}

const QUALIFIER_LABELS: Record<SpfQualifier, string> = {
  '+': 'Pass',
  '-': 'Fail',
  '~': 'SoftFail',
  '?': 'Neutral',
};

const QUALIFIERS: SpfQualifier[] = ['+', '-', '~', '?'];

/** Mechanisms that each consume one of SPF's 10 permitted DNS lookups (RFC 7208 §4.6.4). */
const DNS_LOOKUP_TYPES: SpfMechanismType[] = ['include', 'a', 'mx', 'ptr', 'exists'];

const isQualifier = (ch: string): ch is SpfQualifier => (QUALIFIERS as string[]).includes(ch);

/** Describe a sender-authorizing mechanism in plain English (qualifier-independent). */
function describeMechanism(type: SpfMechanismType, value: string | null): string {
  switch (type) {
    case 'all':
      return 'Matches every sender (the final policy)';
    case 'include':
      return value ? `Authorize the senders listed in ${value}'s SPF record` : 'Include another domain’s SPF record';
    case 'a':
      return value
        ? `Authorize the A/AAAA addresses of ${value}`
        : 'Authorize the domain’s own A/AAAA addresses';
    case 'mx':
      return value ? `Authorize the MX hosts of ${value}` : 'Authorize the domain’s own MX hosts';
    case 'ip4':
      return value ? `Authorize IPv4 ${value}` : 'Authorize an IPv4 range';
    case 'ip6':
      return value ? `Authorize IPv6 ${value}` : 'Authorize an IPv6 range';
    case 'exists':
      return value ? `Authorize when ${value} resolves (advanced)` : 'Authorize when a domain resolves (advanced)';
    case 'ptr':
      return value
        ? `Authorize hosts whose PTR is under ${value} (deprecated)`
        : 'Authorize hosts by reverse DNS (deprecated, discouraged)';
    default:
      return 'Unrecognized mechanism';
  }
}

/** Classify the part of a directive after its (optional) qualifier into a type + value. */
function classifyMechanism(body: string): { type: SpfMechanismType; value: string | null } {
  const lower = body.toLowerCase();

  if (lower === 'all') return { type: 'all', value: null };

  // include / ip4 / ip6 / exists always carry a ':' argument.
  for (const name of ['include', 'ip4', 'ip6', 'exists'] as const) {
    if (lower.startsWith(`${name}:`)) return { type: name, value: body.slice(name.length + 1) || null };
  }

  // a / mx / ptr may be bare, or carry a ':' domain and/or a '/' CIDR.
  for (const name of ['a', 'mx', 'ptr'] as const) {
    if (lower === name) return { type: name, value: null };
    if (lower.startsWith(`${name}:`)) return { type: name, value: body.slice(name.length + 1) || null };
    // `a/24`, `mx//64` — dual-CIDR with no domain; keep the CIDR (with its slash) as the value.
    if (name !== 'ptr' && lower.startsWith(`${name}/`)) return { type: name, value: body.slice(name.length) || null };
  }

  return { type: 'unknown', value: body || null };
}

/** Detect a modifier token of the form `name=value` (name = ALPHA *( ALPHA / DIGIT / "-" / "_" / "." )). */
function asModifier(token: string): SpfModifier | null {
  const match = /^([a-zA-Z][a-zA-Z0-9._-]*)=(.*)$/.exec(token);
  if (!match) return null;

  const name = match[1].toLowerCase();
  const value = match[2];

  if (name === 'redirect') {
    return { raw: token, type: 'redirect', name: 'redirect', value, description: `Delegate the entire policy to ${value || 'another domain'}` };
  }
  if (name === 'exp') {
    return { raw: token, type: 'exp', name: 'exp', value, description: `Explanation string published at ${value || 'another domain'}` };
  }
  return { raw: token, type: 'unknown', name, value, description: `Unknown modifier "${name}" (ignored by receivers)` };
}

/** Map an `all`/redirect qualifier to a final-policy description + severity. */
function buildPolicy(qualifier: SpfQualifier, delegated: boolean, redirectTarget: string | null): SpfPolicy {
  if (delegated) {
    return {
      qualifier,
      label: 'Delegated',
      description: `The final policy is delegated to ${redirectTarget ?? 'another domain'} via redirect`,
      severity: 'info',
      delegated: true,
    };
  }

  switch (qualifier) {
    case '-':
      return {
        qualifier,
        label: 'Fail (reject)',
        description: 'Mail from any other source is rejected — the strictest, recommended setting.',
        severity: 'success',
        delegated: false,
      };
    case '~':
      return {
        qualifier,
        label: 'SoftFail (mark)',
        description: 'Mail from any other source is accepted but flagged as suspicious — common during rollout.',
        severity: 'info',
        delegated: false,
      };
    case '?':
      return {
        qualifier,
        label: 'Neutral',
        description: 'No assertion is made about other sources — provides little protection.',
        severity: 'warning',
        delegated: false,
      };
    case '+':
    default:
      return {
        qualifier,
        label: 'Pass (allow all)',
        description: 'Every source is authorized — this defeats the purpose of SPF.',
        severity: 'danger',
        delegated: false,
      };
  }
}

/**
 * Parse an SPF policy string into its structured decomposition. Returns `{ valid: false }` (with empty
 * collections) for anything that is not an SPF record, so callers can render an "invalid record" notice.
 */
export function parseSpf(input: string | null | undefined): ParsedSpf {
  const raw = (input ?? '').trim();

  const empty: ParsedSpf = {
    valid: false,
    version: null,
    mechanisms: [],
    all: null,
    policy: null,
    modifiers: [],
    redirect: null,
    warnings: [],
    raw,
  };

  if (!raw) return empty;

  const tokens = raw.split(/\s+/);
  const first = tokens[0].toLowerCase();
  if (first !== 'v=spf1') {
    return { ...empty, warnings: ['This is not an SPF record (it must begin with "v=spf1").'] };
  }

  const mechanisms: SpfMechanism[] = [];
  const allMechanisms: SpfMechanism[] = [];
  const modifiers: SpfModifier[] = [];
  const warnings: string[] = [];

  let sawAll = false;
  let ignoredAfterAll = 0;

  for (const token of tokens.slice(1)) {
    if (!token) continue;

    // Modifiers (`name=value`) are order-independent and never carry a qualifier; handle them first so a
    // value containing '=' isn't mistaken for a qualified mechanism, and so a modifier placed after `all`
    // is still recorded (unlike mechanisms, which are positional and stop being evaluated at `all`).
    const modifier = asModifier(token);
    if (modifier) {
      modifiers.push(modifier);
      continue;
    }

    let qualifier: SpfQualifier = '+';
    let body = token;
    if (isQualifier(token[0])) {
      qualifier = token[0];
      body = token.slice(1);
    }

    if (!body) {
      warnings.push(`Malformed term "${token}" was ignored.`);
      continue;
    }

    const { type, value } = classifyMechanism(body);
    const mechanism: SpfMechanism = {
      raw: token,
      qualifier,
      qualifierLabel: QUALIFIER_LABELS[qualifier],
      type,
      value,
      description: describeMechanism(type, value),
    };

    if (type === 'all') {
      allMechanisms.push(mechanism);
      sawAll = true;
      continue;
    }

    // RFC 7208 §5.1: directives are checked left-to-right and matching stops at `all`, so any mechanism
    // after the first `all` is dead. Drop it from the permitted-senders list rather than showing a sender
    // that has no effect (a modifier after `all` is fine — handled above).
    if (sawAll) {
      ignoredAfterAll += 1;
      continue;
    }

    mechanisms.push(mechanism);
    if (type === 'unknown') {
      warnings.push(`Unrecognized term "${token}" — receivers may treat the record as invalid.`);
    }
  }

  const all = allMechanisms[0] ?? null;
  const redirectModifier = modifiers.find((m) => m.type === 'redirect') ?? null;
  const redirect = redirectModifier?.value ?? null;

  // RFC 7208: a `redirect` is only consulted when no `all` mechanism is present.
  let policy: SpfPolicy | null = null;
  if (all) {
    policy = buildPolicy(all.qualifier, false, null);
  } else if (redirect) {
    policy = buildPolicy('?', true, redirect);
  }

  // --- Advisories ---------------------------------------------------------
  if (allMechanisms.length > 1) {
    warnings.push('Multiple "all" mechanisms are present; only the first takes effect.');
  }
  if (all && all.qualifier === '+') {
    warnings.push('"+all" authorizes every sender and effectively disables SPF.');
  }
  if (!all && !redirect) {
    warnings.push('No "all" mechanism or "redirect" modifier — the policy defaults to neutral (?all).');
  }
  if (all && redirect) {
    warnings.push('The "redirect" modifier is ignored because an "all" mechanism is present.');
  }
  if (ignoredAfterAll > 0) {
    warnings.push(
      `${ignoredAfterAll} term${ignoredAfterAll === 1 ? '' : 's'} after "all" ${
        ignoredAfterAll === 1 ? 'is' : 'are'
      } ignored — evaluation stops at "all".`,
    );
  }
  if (mechanisms.some((m) => m.type === 'ptr')) {
    warnings.push('The "ptr" mechanism is deprecated (RFC 7208) and should be avoided.');
  }
  // A `redirect` only consumes a DNS lookup when it is actually consulted, i.e. when no `all` is present
  // (RFC 7208 §6.1) — counting an ignored redirect would raise a false "permerror" advisory.
  const lookupCount =
    mechanisms.filter((m) => DNS_LOOKUP_TYPES.includes(m.type)).length + (redirect && !all ? 1 : 0);
  if (lookupCount > 10) {
    warnings.push(
      `This record needs ${lookupCount} DNS lookups; SPF allows at most 10, so verifiers will return "permerror".`,
    );
  }

  return {
    valid: true,
    version: 'spf1',
    mechanisms,
    all,
    policy,
    modifiers,
    redirect,
    warnings,
    raw,
  };
}

// ─── Builder (wizard) helpers ─────────────────────────────────────────────
//
// The inverse of parseSpf: turn an editable list of sender rows + a final policy into a record string,
// and (for prefill) turn an existing record back into rows. Used by the SPF builder wizard.

/** The sender-mechanism types the builder UI offers (`ptr`/`redirect` are intentionally excluded). */
export type SpfBuilderType = 'include' | 'ip4' | 'ip6' | 'a' | 'mx' | 'exists';

/** The sender types, in the order the builder's type menu lists them. */
export const SPF_BUILDER_TYPES: readonly SpfBuilderType[] = ['include', 'ip4', 'ip6', 'a', 'mx', 'exists'];

/** The terminating `all` policy the builder offers. */
export type SpfAllPolicy = '-all' | '~all' | '?all' | '+all';

/** One sender row in the builder. `id` is a stable React key owned by the component. */
export interface SpfSenderRow {
  id: string;
  type: SpfBuilderType;
  value: string;
}

/** `true` for types whose value is mandatory (`ip4`/`ip6`/`include`/`exists`); `a`/`mx` may be bare. */
export function spfTypeRequiresValue(type: SpfBuilderType): boolean {
  return type !== 'a' && type !== 'mx';
}

/** State the builder is seeded with when editing an existing record (see {@link spfToBuilder}). */
export interface SpfBuilderState {
  rows: Array<{ type: SpfBuilderType; value: string }>;
  policy: SpfAllPolicy;
  /**
   * Tokens from the source record the builder can't represent (`ptr`, `redirect`/`exp` modifiers,
   * non-Pass qualifiers, unknown terms). Surfaced so the UI can warn that rebuilding would drop them.
   */
  unsupported: string[];
}

const ALL_POLICY_BY_QUALIFIER: Record<SpfQualifier, SpfAllPolicy> = {
  '+': '+all',
  '-': '-all',
  '~': '~all',
  '?': '?all',
};

/** Serialize one sender row into its mechanism token, or `null` when a required value is still empty. */
function senderToken(row: Pick<SpfSenderRow, 'type' | 'value'>): string | null {
  const value = row.value.trim();
  if (!value) {
    return spfTypeRequiresValue(row.type) ? null : row.type; // bare `a` / `mx`
  }
  // `a`/`mx` may carry a bare dual-CIDR (`/24`, `//64`) with no domain — that form takes no colon.
  if ((row.type === 'a' || row.type === 'mx') && value.startsWith('/')) {
    return `${row.type}${value}`;
  }
  return `${row.type}:${value}`;
}

/**
 * Assemble a sender list + final policy into a full SPF record string. Rows whose required value is
 * still empty are skipped, so the live preview reflects only complete mechanisms.
 */
export function serializeSpf(
  rows: ReadonlyArray<Pick<SpfSenderRow, 'type' | 'value'>>,
  policy: SpfAllPolicy,
): string {
  const tokens = rows.map(senderToken).filter((t): t is string => t !== null);
  return ['v=spf1', ...tokens, policy].join(' ');
}

/**
 * Decompose an existing SPF record into builder state for prefill. Supported sender mechanisms become
 * rows; anything the builder can't edit goes into `unsupported`. A blank/invalid input yields no rows
 * and the safe `~all` default.
 */
export function spfToBuilder(value: string | null | undefined): SpfBuilderState {
  const parsed = parseSpf(value);
  const rows: Array<{ type: SpfBuilderType; value: string }> = [];
  const unsupported: string[] = [];

  if (!parsed.valid) {
    return { rows, policy: '~all', unsupported };
  }

  for (const m of parsed.mechanisms) {
    const supported = m.qualifier === '+' && (SPF_BUILDER_TYPES as readonly string[]).includes(m.type);
    // A bare dual-CIDR `a`/`mx` (`a/24`, `mx//64`) has no domain field in the builder UI — leave it as-is.
    const cidrOnly = (m.type === 'a' || m.type === 'mx') && (m.value?.startsWith('/') ?? false);
    if (supported && !cidrOnly) {
      rows.push({ type: m.type as SpfBuilderType, value: m.value ?? '' });
    } else {
      unsupported.push(m.raw); // non-Pass qualifier, ptr, dual-CIDR a/mx, or unknown mechanism
    }
  }
  for (const mod of parsed.modifiers) {
    unsupported.push(mod.raw); // redirect / exp / unknown modifiers
  }

  return {
    rows,
    policy: parsed.all ? ALL_POLICY_BY_QUALIFIER[parsed.all.qualifier] : '~all',
    unsupported,
  };
}

/**
 * Normalize a TXT record value from PowerDNS presentation form to its logical string.
 *
 * PowerDNS returns TXT content as one or more double-quoted, backslash-escaped character-strings
 * (e.g. `"v=spf1 …"`, or `"chunk1" "chunk2"` for long values), and the zone read returns it verbatim.
 * This strips the quoting and concatenates the chunks so callers can match on the logical value (e.g.
 * to detect an existing `v=spf1` line among apex TXT records). A value that isn't in quoted form is
 * returned trimmed and unchanged.
 */
export function unwrapTxtValue(raw: string): string {
  const s = raw.trim();
  if (!s.startsWith('"')) return s;

  let out = '';
  let i = 0;
  while (i < s.length) {
    if (s[i] !== '"') {
      i += 1; // whitespace between quoted chunks
      continue;
    }
    i += 1; // opening quote
    while (i < s.length && s[i] !== '"') {
      if (s[i] === '\\' && i + 1 < s.length) {
        out += s[i + 1]; // unescape \" and \\
        i += 2;
      } else {
        out += s[i];
        i += 1;
      }
    }
    i += 1; // closing quote
  }
  return out;
}
